<template>
    <div :class="['field', { required: required }]">
        <transition name="fade">
            <label v-if="title" :title="title">
                {{ title }}
                <tooltip v-if="hint" :text="hint"></tooltip>
            </label>
        </transition>
        <input
                ref="input"
                style="width: 100%; border: none;"
                type="text"
                :name="name"/>
    </div>
</template>

<script>
    import Tooltip from '@/components/tooltip/tooltip';
    import _ from 'lodash';

    export default {
        props: {
            dataSource: {
                type: Array,
                default: () => [{}]
            },
            label: String,
            hint: String,
            name: String,
            description: String,
            placeholder: [Boolean, String],
            required: [Boolean, String],
            groupBy: [String],
            listWidth: {
                type: Number
            },
            index: {
                type: Number,
                default: 0
            },
            dataTextField: {
                type: String,
                default: 'text'
            },
            dataValueField: {
                type: String,
                default: 'value'
            },
            enable: {
                type: Boolean,
                default: true
            },
            template: {
                type: String,
                default: ''
            },
            value: {
                type: [Object, Number]
            }
        },

        data () {
            const index = this.getIndex();

            const config = {
                dataTextField: this.dataTextField,
                dataValueField: this.dataValueField,
                dataSource: {
                    data: this.dataSource || [{ text: 'Vazio', value: 'Vazio' }]
                },
                enable: this.enable,
                template: this.template,
                index,
                change: this.onChange,
                filter: _.size(this.dataSource) > 5 ? 'contains' : 'none'
            };

            if (this.placeholder) {
                config.optionLabel = this.placeholder;
            }

            if (this.groupBy) {
                config.dataSource.group = {
                    field: this.groupBy
                };
            }

            return {
                title: this.label,
                config,
                widget: undefined
            };
        },

        mounted () {
            this.widget = new kendo.ui.DropDownList(this.$refs.input, this.config);

            if (this.listWidth) {
                this.widget.list.width(this.listWidth);
            }
        },

        destroyed () {
            this.widget.destroy();
        },

        methods: {
            getIndexValue (val) {
                if (_.isObject(val)) {
                    const dataItems = this.widget ? this.widget.dataItems() : this.dataSource;
                    const index = _.findIndex(dataItems, d => {
                        if (_.isArray(val.value)) {
                            return _.some(val.value, v => this.normalize(v) === this.normalize(d[this.dataValueField]));
                        }
                        return this.normalize(val.value) === this.normalize(d[this.dataValueField]);
                    }) + (this.placeholder ? 1 : 0);
                    return index || 0;
                }
                return this.index;
            },

            getIndex (value = this.value) {
                return (_.isObject(value) && this.hasValue(value.value)) ? this.getIndexValue(value) : (_.isString(value) || _.isNumber(value)) ? this.getIndexValue({ value }) : this.index;
            },

            onChange () {
                const data = this.formatData();
                this.$emit('input', data);
                this.$emit('change', data);
            },

            hasValue (value) {
                return _.isArray(value) ? _.size(value) : !isNaN(Number(value)) ? true : !!value;
            },

            normalize (value) {
                if (!isNaN(Number(value))) {
                    return Number(value);
                }

                return value ? _.chain(String(value)).trim().deburr().toUpper().value() : '';
            },

            formatData () {
                const value = this.widget.value();
                return {
                    metadata: {
                        name: this.name,
                        text: this.dataTextField,
                        value: this.dataValueField,
                        properties: Object.keys(this.dataSource[0] || {})
                    },
                    value: isNaN(Number(value)) ? value : Number(value),
                    text: this.widget.text()
                };
            }
        },

        watch: {
            value (newVal, oldVal) {
                if (!_.isEqual(newVal, oldVal)) {
                    this.widget.select(this.getIndex(newVal));
                }
            },

            dataSource (val) {
                if (!_.size(val)) {
                    return;
                } else if (_.size(val) > 5) {
                    this.widget.setOptions({ filter: 'contains' });
                } else {
                    this.widget.setOptions({ filter: 'none' });
                }

                const dataSource = { data: val };

                if (this.groupBy) {
                    dataSource.group = {
                        field: this.groupBy
                    };
                }

                this.widget.setDataSource(new kendo.data.DataSource(dataSource));
                this.widget.select(this.getIndex(this.value));
            },

            label (val) {
                this.title = val;
            },

            dataTextField (val) {
                this.widget.setOptions({ dataTextField: val });
            },

            dataValueField (val) {
                this.widget.setOptions({ dataValueField: val });
            },

            enable (val) {
                this.widget.setOptions({ enable: val });
                this.widget.enable(val);
            }
        },

        components: {
            Tooltip
        }
    };
</script>

<style lang="scss" scoped>
    // Animations
    .fade-enter-active, .fade-leave-active {
        transition: opacity .5s
    }

    .fade-enter, .fade-leave-active {
        opacity: 0
    }
</style>
